import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
// 0.10: Kataloge + Sprachliste liegen in core (plattformfrei); dieser
// Init mit Browser-Detector + document-Zugriff bleibt bewusst Web-seitig.
import { de } from '@sizilianer/core/i18n/de';
import { it } from '@sizilianer/core/i18n/it';
import { en } from '@sizilianer/core/i18n/en';
import {
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from '@sizilianer/core/i18n/languages';

export { SUPPORTED_LANGUAGES };
export type { SupportedLanguage };

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      de: { translation: de },
      it: { translation: it },
      en: { translation: en },
    },
    fallbackLng: 'de',
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'sizilianer-language',
      caches: ['localStorage'],
    },
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  });

// WCAG 3.1.1: <html lang> muss der tatsächlichen Seitensprache folgen —
// index.html setzt statisch "de", hier ziehen wir Umschaltungen (de/it/en) nach.
i18n.on('languageChanged', (lng) => {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = (lng || 'de').slice(0, 2);
  }
});

/**
 * Aktuelle Gast-Sprache als 2-Letter-Code für API-Payloads (6B: Mail-
 * Lokalisierung). Immer eines von de/it/en — alles andere fällt auf 'de',
 * damit serverseitige Vokabular-Checks nie eine Buchung abweisen.
 */
export function currentGuestLanguage(): SupportedLanguage {
  const lng = (i18n.language || 'de').slice(0, 2).toLowerCase();
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(lng)
    ? (lng as SupportedLanguage)
    : 'de';
}

/**
 * Helper: Pickt das richtige lokalisierte Feld einer DB-Row.
 * Beispiel: pickLocalized({ name_de, name_it, name_en }, 'name', currentLng)
 */
export function pickLocalized<T extends Record<string, unknown>>(
  row: T,
  baseField: string,
  lng: string,
): string | null {
  // Reihenfolge: lng → de → en → it (Restaurant ist in Dorsten, deutsche default)
  const cleanLng = (lng || 'de').slice(0, 2).toLowerCase();
  const suffixes = [cleanLng];
  if (cleanLng !== 'de') suffixes.push('de');
  if (cleanLng !== 'en' && cleanLng !== 'de') suffixes.push('en');

  for (const s of suffixes) {
    const key = s === 'de' ? baseField : `${baseField}_${s}`;
    const value = row[key];
    if (value != null && value !== '') return String(value);
  }
  // base ohne suffix als final fallback (z.B. name=DE)
  const fallback = row[baseField];
  return fallback != null ? String(fallback) : null;
}

export default i18n;
